import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Table, Icon, Message, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import DesignTemplateModel from '../../../../../models/designTemplateModel';
import DesignEmpty from '../design-row/designEmpty';

import * as Sentry from '@sentry/browser';
import Style from './templateModale.module.css';

function DeleteSubtitle({ templates }) {
    const { t } = useTranslation();

    return (
        <>
            <span>{t('ticket.ticket_design.templates.modale.delete.confirm', { count: templates?.length || 0 })}</span>
            <span>{t('ticket.ticket_design.templates.modale.cannot_undone')}</span>
        </>
    );
}

function DeleteComponent({ templates, designs }) {
    const { t } = useTranslation();

    const linkedDesigns = React.useMemo(() => designs.filter(d => templates.some(t => d.template_id === t.id)), [designs, templates]);
    return (
        linkedDesigns.length > 0 && (
            <Message type="danger" title={t(`ticket.ticket_design.templates.modale.delete.warning_title`)}>
                {t(`ticket.ticket_design.templates.modale.delete.warning_subtitle`)}
            </Message>
        )
    );
}

function getDesignLink(design, organization) {
    return (
        <a href={`/ticket/O${organization.id}/tickets_design/designs/${design.id}`} target="_blank" rel="noopener noreferrer">
            <Icon name="external-link" />
        </a>
    );
}

function PreviewSubtitle({ designs }) {
    const { t } = useTranslation();

    return t('ticket.ticket_design.templates.modale.design_subtitle', { count: designs?.length || 0 });
}

function PreviewComponent({ organization, designs }) {
    const { t } = useTranslation();

    return (
        <>
            <Table useNew className={Style['list__table']}>
                <Table.THead>
                    <Table.Tr>
                        <th>{t('ticket.ticket_design.designs.label')}</th>
                        <th />
                    </Table.Tr>
                </Table.THead>
                <Table.TBody>
                    {designs.map((design, key) => (
                        <Table.Tr key={key}>
                            <Table.Td>{design.name}</Table.Td>
                            <Table.Td className="external-links">
                                <div className="link">{getDesignLink(design, organization)}</div>
                            </Table.Td>
                        </Table.Tr>
                    ))}
                </Table.TBody>
            </Table>
            {designs.length === 0 && <DesignEmpty />}
        </>
    );
}

function DuplicateComponent({ duplicateName = '', placeholderName = '', setDuplicateName = () => {}, setCanSubmit = () => {} }) {
    const { t } = useTranslation();

    return (
        <>
            <Form.Field>
                <Form.Label label={t('ticket.ticket_design.templates.modale.new_name')} />
                <Form.Input
                    required
                    name="duplicate_name"
                    placeholder={placeholderName}
                    value={duplicateName}
                    onChange={({ target: { value } }) => {
                        setCanSubmit(value?.length > 0 && value?.length < 60);
                        setDuplicateName(value);
                    }}
                />
            </Form.Field>
        </>
    );
}

export function TemplateModale({ type = '', designs = [], templates = [], setSelectedTemplates = () => {}, setShowModale = () => {}, organization, sync }) {
    const { t } = useTranslation();

    const placeholderName = React.useMemo(() => {
        const name = templates.length && templates[0].name;
        const placeholder = `${name} - ${t('ticket.common.copy')}`;
        return placeholder?.length < 60 ? placeholder : name;
    }, [templates]);
    const [duplicateName, setDuplicateName] = React.useState(placeholderName);
    const [canSubmit, setCanSubmit] = React.useState(true);

    const defaultTitle = t(`ticket.ticket_design.templates.modale.${type}.cta`, { name: templates.length && templates[0].name });
    const previewTitle = t(`ticket.ticket_design.templates.modale.design_title`, { count: (designs && designs?.length) || 0 });

    const toast = React.useContext(ToastContext);

    React.useEffect(() => {
        if (designs.length > 0 && type === 'delete') setCanSubmit(false);
    }, [designs, type]);

    const _onClose = () => {
        setShowModale({ action: null, selected: [] });
    };

    const onDuplicate = React.useCallback(
        (template, value) => {
            if (!template) {
                console.error('template to duplicate not found');
                toast.error(t(`ticket.ticket_design.templates.modale.duplicate_error`));
                return;
            }
            const newTemplate = new DesignTemplateModel(template);
            newTemplate.name = duplicateName;
            newTemplate.id = null;
            newTemplate.available_to_all_organizations = false;
            newTemplate
                .save({ organizationId: organization.id })
                .then(() => {
                    toast.success(t(`ticket.ticket_design.templates.modale.duplicate_success`));
                    sync();
                })
                .catch(e => {
                    console.error(e);
                    Sentry.captureException(e);
                    toast.error(t(`ticket.ticket_design.templates.modale.duplicate_error`));
                })
                .finally(() => {
                    setShowModale(false);
                });
        },
        [organization, sync, duplicateName]
    );

    const onDelete = React.useCallback(
        templatesToDelete => {
            if (!templatesToDelete) {
                console.error('template to delete not found');
                toast.error(t(`ticket.ticket_design.templates.modale.delete_error`));
                return;
            }
            DesignTemplateModel.perform_delete(templatesToDelete, { organization })
                .then(() => {
                    toast.success(t(`ticket.ticket_design.templates.modale.delete_success`));
                    setSelectedTemplates([]);
                    sync();
                })
                .catch(e => {
                    console.error(e);
                    Sentry.captureException(e);
                    toast.error(t(`ticket.ticket_design.templates.modale.delete_error`));
                })
                .finally(() => {
                    setShowModale(false);
                });
        },
        [organization, setSelectedTemplates, sync]
    );

    const onSubmit = React.useCallback(() => {
        switch (type) {
            case 'delete':
                onDelete(templates);
                break;
            case 'duplicate':
                onDuplicate(templates[0], duplicateName);
                break;
            default:
                _onClose();
                break;
        }
    }, [type, templates, duplicateName]);

    const getActionComponent = typeComponent => {
        switch (typeComponent) {
            case 'delete':
                return <DeleteComponent templates={templates} designs={designs} />;
            case 'duplicate':
                return (
                    <DuplicateComponent
                        setCanSubmit={setCanSubmit}
                        duplicateName={duplicateName}
                        setDuplicateName={setDuplicateName}
                        placeholderName={placeholderName}
                        template={templates[0]}
                    />
                );
            case 'preview':
                return <PreviewComponent organization={organization} designs={designs} templates={templates} />;
            default:
                return <></>;
        }
    };

    return (
        <Modal
            size={'large'}
            allowExternalClosing={true}
            open={Boolean(type?.length > 0)}
            onClose={_onClose}
            title={type === 'preview' ? previewTitle : defaultTitle}
            subtitle={type === 'preview' ? PreviewSubtitle({ designs }) : type === 'delete' ? DeleteSubtitle({ templates }) : null}
            customFooter={
                <div className={`wz-onboarding-modal-tour__footer`}>
                    <Button.Group>
                        {type !== 'preview' && <Button onClick={_onClose}>{t('ticket.common.action.cancel')}</Button>}
                        <Button disabled={!canSubmit} onClick={onSubmit} primary>
                            {t(type !== 'preview' ? 'ticket.common.action.confirm' : 'ticket.common.action.close')}
                        </Button>
                    </Button.Group>
                </div>
            }
        >
            {getActionComponent(type)}
        </Modal>
    );
}

TemplateModale.propTypes = {
    type: PropTypes.string,
    designs: PropTypes.arrayOf(PropTypes.object),
    templates: PropTypes.arrayOf(PropTypes.object),
    setSelectedTemplates: PropTypes.func,
    setShowModale: PropTypes.func,
    organization: PropTypes.object,
    sync: PropTypes.func
};
